import {getRequest} from '@/Services/ApiService';
import APIURL from '@/api-url';
import DateFormat from '@/components/Common/dateFormat';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import type {LatestInsightPost} from '@/types/listing';
import {colors, fontFamily, fontSize, fontWeight} from '@/styles/tokens';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import {memo, useEffect, useState} from 'react';
import parse from 'html-react-parser';
import {formatListingDate} from '@/util/blogDates';
import {LATEST_INSIGHTS_FIELDS} from '@/Services/latestInsights';
const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));

const titleContainerSx = {height: '50px'};

// Migrated from globals.scss `.insights_card_content p`. Sole owner — this component is the only
// renderer of `.insights_card_content`. The nested `p` isn't a JSX element this component authors
// directly — it comes from the CMS excerpt HTML (`blogItem.excerpt.rendered`) run through
// html-react-parser — so the rule is expressed as a `'& p'` nested selector on this element's own
// `sx` (the Typography carrying the `insights_card_content` className below) rather than a prop on
// a direct `<p>`.
const insightsCardContentSx = {
    '& p': {
        fontFamily: `${fontFamily.body} !important`,
        fontWeight: `${fontWeight.regular} !important`,
        fontSize: `${fontSize[16]} !important`,
        color: colors.text.primary,
        lineClamp: '5 !important',
        WebkitLineClamp: '5 !important',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        height: '120px', // no exact spacing-token match
        overflow: 'hidden'
    }
} as const;

interface LatestInsightsProps {
    id?: string;
    /**
     * Server-rendered posts. When provided (via getStaticProps), the cards are in
     * the initial HTML and the client fetch is skipped. When omitted, the
     * component falls back to fetching on mount (legacy, for un-migrated pages).
     */
    posts?: LatestInsightPost[];
}

const LatestInsights = ({id, posts}: LatestInsightsProps) => {
    const [blogData, setBlogData] = useState<LatestInsightPost[]>(posts ?? []);
    useEffect(() => {
        // SSR-provided posts are already in the HTML — don't re-fetch on the client.
        if (posts !== undefined) return undefined;
        const controller = new AbortController();
        // Only the fields the card renders — the full post payload is ~98 KB for three posts.
        const url = `${id ? `categories=${id}&per_page=3` : '&per_page=3'}&_fields=${LATEST_INSIGHTS_FIELDS}`;
        getRequest<LatestInsightPost[]>(`${APIURL.BLOGS_LISTING}?${url}`, controller.signal)
            .then((res) => {
                setBlogData(Array.isArray(res) ? res : []);
            })
            .catch(() => {
                setBlogData([]);
            });
        return () => controller.abort();
    }, [id, posts]);

    return (
        <Box className="padding-bottom-zero">
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    <Typography variant="h1" component="h2" mb={1.5} fontSize={{xs: 35, sm: 48}} className="font-headers">
                        Our Latest Insights
                    </Typography>
                </Box>
                <Grid container spacing={3}>
                    {blogData?.map((blogItem: any) => {
                        return (
                            <Grid item xs={12} sm={6} md={4} lg={4} key={blogItem.id}>
                                <Box className="insights_card">
                                    <Link
                                        href={`blog/${blogItem?.slug}`}
                                        target=""
                                        className="menu-links"
                                        aria-label={`Read blog: ${blogItem?.title}`}
                                    >
                                        <DisplayImage
                                            src={blogItem?.blog_image}
                                            alt={blogItem?.title}
                                            height={218}
                                            width={300}
                                            className="insights_img"
                                            priority={false}
                                        />
                                    </Link>
                                    <Box className="content">
                                        <Box className="date_icon">
                                            <CalendarMonthIcon />
                                            <Typography className="insights_date font-regular-text">
                                                {formatListingDate(blogItem, DateFormat)}
                                            </Typography>
                                        </Box>
                                        <Box sx={titleContainerSx}>
                                            <Tooltip title={`${blogItem?.title}`} placement="top-start" arrow>
                                                <div>
                                                    <Link href={`blog/${blogItem?.slug}`} target="" className="card-redirection-heading">
                                                        <Typography component="h3" className="insights_card_heading font-headers">
                                                            {blogItem?.title}
                                                        </Typography>
                                                    </Link>
                                                </div>
                                            </Tooltip>
                                        </Box>
                                        <Typography
                                            component="div"
                                            className="insights_card_content font-regular-text"
                                            sx={insightsCardContentSx}
                                        >
                                            {blogItem.excerpt?.rendered ? parse(blogItem.excerpt.rendered) : ''}
                                        </Typography>
                                        <Box className="spectrum-btn">
                                            <Link href={`blog/${blogItem?.slug}`} target="" className="menu-links">
                                                <MUIButton
                                                    label="Read Article"
                                                    variant="contained"
                                                    className="insights_btn font-regular-text"
                                                    aria-label={`Read article: ${blogItem?.title}`}
                                                />
                                            </Link>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
                <Grid container justifyContent="center" pt={4}>
                    <Link href="/blog" className="menu-links">
                        <MUIButton
                            label="View All Blogs"
                            variant="contained"
                            className="animated-button button-bg-primary font-regular-text"
                            aria-label="Book Consultation"
                        />
                    </Link>
                </Grid>
            </MUIContainer>
        </Box>
    );
};
export default memo(LatestInsights);
